// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import type { ProviderFeedbackRequest } from '../../types/providerAccess';
import { PoweredByFinerty } from '../PoweredByFinerty';
import { TextArea } from '@loa/ui-components';
import { useSafeImageSrc } from '../../hooks/useSafeImageSrc';

type FeedbackPageProps = Readonly<{
  brokerageName: string;
  brokerageLogoUrl: string | null;
  submitting: boolean;
  onSubmitFeedback: (request: ProviderFeedbackRequest) => Promise<boolean>;
  /** Reserved — caller wires a "Skip" path; component currently has no
   *  skip button but accepts the prop for future use. */
  onSkip?: () => void;
}>;

const RATING_OPTIONS = [1, 2, 3, 4, 5];

export function FeedbackPage({
  brokerageName,
  brokerageLogoUrl,
  submitting,
  onSubmitFeedback,
}: FeedbackPageProps) {
  const logoSrc = useSafeImageSrc(brokerageLogoUrl);
  const [satisfactionRating, setSatisfactionRating] = useState<number | undefined>(undefined);
  const [easeOfUseRating, setEaseOfUseRating] = useState<number | undefined>(undefined);
  const [comments, setComments] = useState('');

  const canSubmit =
    satisfactionRating !== undefined || easeOfUseRating !== undefined || comments.trim().length > 0;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    const request: ProviderFeedbackRequest = {};
    if (satisfactionRating !== undefined) request.satisfactionRating = satisfactionRating;
    if (easeOfUseRating !== undefined) request.easeOfUseRating = easeOfUseRating;
    if (comments.trim()) request.comments = comments.trim();

    await onSubmitFeedback(request);
  }, [canSubmit, satisfactionRating, easeOfUseRating, comments, onSubmitFeedback]);

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  const renderRatingButtons = (
    selected: number | undefined,
    onSelect: (val: number) => void,
    lowLabel: string,
    highLabel: string,
  ) => (
    <div className="flex items-center gap-sm">
      <span className="text-grey-04 font-sans text-body-03 font-normal leading-normal">{lowLabel}</span>
      {RATING_OPTIONS.map((val) => (
        <button
          key={val}
          type="button"
          onClick={() => onSelect(selected === val ? undefined as unknown as number : val)}
          className={`flex w-[32px] h-[32px] flex-col justify-center items-center rounded-[2.667px] border-none cursor-pointer transition-colors ${
            selected === val
              ? 'bg-purple-primary'
              : 'bg-lilac-05 hover:bg-[#E8E6FF]'
          }`}
        >
          <div className={`text-center font-sans text-body-02 font-normal leading-normal ${
            selected === val ? 'text-white' : 'text-purple-primary'
          }`}>
            {val}
          </div>
        </button>
      ))}
      <span className="text-grey-04 font-sans text-body-03 font-normal leading-normal">{highLabel}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F5]">
      {/* ══════════════ Header ══════════════ */}
      <div className="w-full h-[80px] bg-white border-b border-[#E1E1E1] flex items-center justify-between px-[120px] py-[10px] shrink-0">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={`${brokerageName} logo`}
            className="h-[40px] object-contain"
          />
        ) : (
          <div className="text-black font-sans text-body-01 font-medium">{brokerageName}</div>
        )}
        <PoweredByFinerty size="small" />
      </div>

      {/* ══════════════ Content ══════════════ */}
      <div className="flex-1 flex justify-center">
        <div className="w-full max-w-[920px] mx-[120px] my-xxl">
          <div className="bg-white rounded-card p-lg flex flex-col gap-xxl">
            {/* Title */}
            <div className="flex flex-col items-center gap-sm">
              <div className="text-black font-sans text-h4 font-normal leading-normal text-center">
                Policy Details Submitted!
              </div>
              <div className="text-black font-sans text-body-02 font-normal leading-normal text-center">
                Thank you for filling out the policy details. If you have 5 mins we would love to hear your feedback!
              </div>
            </div>

            {/* How satisfied are you */}
            <div className="flex flex-col gap-[12px]">
              <div className={labelClass}>
                How satisfied are you with the platform?
              </div>
              {renderRatingButtons(satisfactionRating, setSatisfactionRating, 'Very dissatisfied', 'Very satisfied')}
            </div>

            {/* How easy was it */}
            <div className="flex flex-col gap-[12px]">
              <div className={labelClass}>
                How easy was it to use the platform?
              </div>
              {renderRatingButtons(easeOfUseRating, setEaseOfUseRating, 'Very difficult', 'Very easy')}
            </div>

            {/* Comments */}
            <div className="flex flex-col gap-sm">
              <TextArea
                label="Is there anything else you'd like to share about your experience with the platform?"
                placeholder="Write your comments here..."
                maxLength={2000}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                fluid
              />
            </div>

            {/* Submit button */}
            <div className="flex justify-end">
              <button
                type="button"
                disabled={submitting || !canSubmit}
                onClick={handleSubmit}
                className={`h-[48px] px-xl rounded-button border-none transition-colors ${
                  submitting
                    ? 'bg-[#E1E1E1] cursor-not-allowed'
                    : 'bg-purple-primary cursor-pointer hover:bg-[#1E0633] active:bg-[#150428]'
                }`}
              >
                <div className={`font-sans text-body-02 font-medium leading-normal not-italic capitalize ${
                  submitting ? 'text-[#888]' : 'text-white'
                }`}>
                  {submitting ? 'Loading...' : 'Submit'}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════ Footer ══════════════ */}
      <div className=" h-[80px] w-full bg-white border-t border-[#E1E1E1] flex items-center justify-between px-[120px] py-md shrink-0">
        <div className="text-black font-sans text-body-04 font-normal leading-normal">
          Copyrights © 2026 Finerty Data Limited. All Rights Reserved.
        </div>
        <div className="flex items-center gap-[10px]">
          <a
            href="https://www.finertydata.com/legal/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center"
          >
            <div className="text-black font-sans text-body-04 font-normal leading-normal underline">
              Privacy Policy
            </div>
          </a>
          <div className="w-[1px] h-[15px] bg-black" />
          <a
            href="https://www.finertydata.com/legal/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center"
          >
            <div className="text-black font-sans text-body-04 font-normal leading-normal underline">
              Terms &amp; Conditions
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
